import React, { FC } from 'react';

import Typography from '@mui/material/Typography';

import { authorizationActions } from '@contexts/common/AuthorizationContext';

import coinbaseIcon from '@assets/icons/Coinbase.svg?url';
import safePalIcon from '@assets/icons/SafePal.svg?url';
import injectedIcon from '@assets/icons/inject.svg?url';
import metamaskIcon from '@assets/icons/metamask.svg?url';
import rabbyIcon from '@assets/icons/rabby.svg?url';
import walletConnectIcon from '@assets/icons/walletConnect.svg?url';

import ModalWrapper from '@components/common/Modal/ModalWrapper';

import { ConnectorNames } from '@constants/common/wallet.constants';

import useStyles from './styles.module';

const isMetamaskInstalled = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (window.ethereum?.isMetaMask) {
    return true;
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  if (window.ethereum?.providers?.some((p: any) => p?.isMetaMask)) {
    return true;
  }

  return false;
};

export enum BadgeType {
  QrCode = 'QrCode',
  Installed = 'Installed',
  NotInstalled = 'NotInstalled',
}

interface BadgeProps {
  type: BadgeType;
}

const Badge: FC<BadgeProps> = ({ type }) => {
  const { classes, cx } = useStyles();

  return (
    <>
      {type === BadgeType.QrCode && (
        <div className={cx(classes.badge, classes.qrCode)}>
          <Typography
            variant="caption2"
            color={(theme) => theme.palette.custom.tags.text}
          >
            QR CODE
          </Typography>
        </div>
      )}
      {type === BadgeType.Installed && (
        <div className={cx(classes.badge, classes.installed)}>
          <Typography
            variant="caption2"
            color={(theme) => theme.palette.custom.tags.text}
          >
            INSTALLED
          </Typography>
        </div>
      )}
      {type === BadgeType.NotInstalled && (
        <div className={cx(classes.badge, classes.notInstalled)}>
          <Typography
            variant="caption2"
            color={(theme) => theme.palette.custom.tags.text}
          >
            NOT INSTALLED
          </Typography>
        </div>
      )}
    </>
  );
};

const WalletConnectModal: FC = () => {
  const { classes } = useStyles();

  const wallets = [
    {
      name: 'WalletConnect',
      icon: <img src={walletConnectIcon} alt="" className={classes.icon} />,
      connectorId: ConnectorNames.WalletConnect,
    },
    {
      name: 'Metamask',
      icon: <img src={metamaskIcon} alt="" className={classes.icon} />,
      connectorId: ConnectorNames.MetaMask,
      get installed() {
        return isMetamaskInstalled();
      },
    },
    {
      name: 'Rabby Wallet',
      icon: <img src={rabbyIcon} alt="" className={classes.icon} />,
      connectorId: ConnectorNames.Injected,
      get installed() {
        return (
          typeof window !== 'undefined' && Boolean(window.ethereum?.isRabby)
        );
      },
    },
    {
      name: 'Coinbase Wallet',
      icon: <img src={coinbaseIcon} alt="" className={classes.icon} />,
      connectorId: ConnectorNames.WalletLink,
    },
    {
      name: 'SafePal',
      icon: <img src={safePalIcon} alt="" className={classes.icon} />,
      connectorId: ConnectorNames.Injected,
      get installed() {
        return (
          typeof window !== 'undefined' && Boolean(window.ethereum?.isSafePal)
        );
      },
    },
    // {
    //   name: 'Trust Wallet',
    //   icon: <RabbyIcon />,
    //   connectorId: ConnectorNames.Injected,
    //   get installed() {
    //     return typeof window !== 'undefined' && Boolean(window.ethereum?.isRabby);
    //   },
    // },
    {
      name: 'Injective',
      icon: <img src={injectedIcon} alt="" className={classes.icon} />,
      connectorId: ConnectorNames.Injected,
      installed: typeof window !== 'undefined' && Boolean(window.ethereum),
    },
  ];

  return (
    <ModalWrapper title="Connect wallet">
      <div className={classes.container}>
        {wallets.map((item) => {
          const type =
            item.connectorId === ConnectorNames.WalletConnect ||
            item.connectorId === ConnectorNames.WalletLink
              ? BadgeType.QrCode
              : item.installed
                ? BadgeType.Installed
                : BadgeType.NotInstalled;

          return (
            <button
              className={classes.button}
              key={item.name}
              onClick={() => authorizationActions.handleLogIn(item.connectorId)}
              disabled={
                item.connectorId !== ConnectorNames.WalletConnect &&
                item.connectorId !== ConnectorNames.WalletLink &&
                !item.installed
              }
            >
              <div className={classes.row}>
                {item.icon}
                <Typography
                  variant="subtitle1"
                  color={(theme) => theme.palette.custom.text.dark}
                >
                  {item.name}
                </Typography>
              </div>
              {item.name !== 'Injective' && item.name !== 'Coinbase Wallet' && (
                <Badge type={type} />
              )}
            </button>
          );
        })}
      </div>
    </ModalWrapper>
  );
};

export default WalletConnectModal;
